<template>
  <a
    class="sanitas-search-result"
    :href="href"
    target="_blank"
    rel="noopener"
    onclick="if (typeof s !== 'undefined'){s.tl(this,'e','ComSearchResultClick',null,'navigate')}"
  >
    <article class="card">
      <img
        v-if="type === 'product'"
        class="product-image"
        :src="imageUrl"
        :alt="imageAlt"
      />
      <div class="content">
        <h3 id="title" class="title" v-html="title"></h3>

        <dotcom-icon
          v-if="type === 'document'"
          name="file"
          art="light"
          color="--c-secondary-neutral-2"
          hover-color="--c-secondary-neutral-2"
          class="document-icon"
        />

        <p
          id="description"
          :class="{
            'description': true,
            'document-description': type === 'document',
          }"
          v-html="description"
        ></p>
      </div>
      <dotcom-icon
        v-if="type === 'document'"
        name="circle-arrow-down"
        art="light"
        class="download-icon"
      />
    </article>
  </a>
</template>

<script>
export default {
  name: 'SanitasSearchResult',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    type: {
      type: String,
      default: '',
    },

    /**
     * link/page that will be opened when the user taps the search result
     */
    href: {
      type: String,
      default: '',
    },

    /**
     * the title of the result
     *
     * You can highlight portions of the title by encapsulating the wanted
     * part with a ```<strong>_content_to_highlight_</strong>```
     *
     * Please note that the String passed here will be directly injected as HTML
     * in the document
     **/
    title: {
      type: String,
      default: '',
    },

    /**
     * a small description shown underneath the title.
     *
     * You can highlight portions of the description by encapsulating the wanted
     * part with a ```<strong>_content_to_highlight_</strong>```
     *
     * Please note that the String passed here will be directly injected as HTML
     * in the document
     **/
    description: {
      type: String,
      default: '',
    },

    /**
     * url of the image being shown in the result
     *
     * Note: it is only used in type `product`
     **/
    imageUrl: {
      type: String,
      default: '',
    },

    /**
     * alt of the image being shown in the result
     *
     * Note: it is only used in type `product`
     */
    imageAlt: {
      type: String,
      default: '',
    },
  },
}
</script>

<style lang="postcss">
.sanitas-search-result {
  display: block;
  text-decoration: none;
  margin-bottom: var(--spacing-2);
  border: 0;

  &:hover {
    border: 0;
  }

  .card {
    display: flex;
    padding: var(--spacing-6);
    border-radius: var(--border-radius);
    background-color: var(--c-primary-neutral-2);

    &:hover {
      background-color: var(--c-primary-neutral-3);
      box-shadow: 0 0 10px var(--c-primary-neutral-1);

      .document-download-icon {
        color: var(--c-primary-color-2);
      }
    }
  }

  .content {
    flex-grow: 20;
    overflow: hidden;

    .dotcom-icon {
      width: 15px;
      height: 15px;
      vertical-align: middle;
      padding-right: var(--spacing-1);
    }
  }

  .title {
    font-weight: 700;
    font-style: normal;
    font-size: 15px;
    line-height: 1.35;
    letter-spacing: 0;
    color: var(--c-primary-neutral-1);
    margin: 0;
    margin-right: var(--spacing-2);
    word-break: break-all;
  }

  .description {
    font-weight: 400;
    font-style: normal;
    font-size: 15px;
    line-height: 1.35;
    letter-spacing: normal;
    color: var(--c-primary-neutral-1);
    margin: 0;
  }

  .title,
  .description {
    strong {
      background-color: var(--c-primary-color-1-c);
    }
  }

  .product-image {
    display: none;
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 50%;
    margin-right: 15px;
  }

  .document-description {
    font-weight: 400;
    font-style: normal;
    font-size: 13px;
    line-height: 1.35;
    letter-spacing: normal;
    color: var(--c-primary-neutral-1);

    margin: 0;
    display: inline-block;
  }

  .document-icon {
    font-size: 20px;
  }

  .download-icon {
    font-size: 18px;
  }

  @media (--md) {
    .product-image {
      display: inline-block;
    }
  }
}
</style>
